import {
  Accordion,
  ActionIcon,
  Alert,
  AppShell,
  Badge,
  Box,
  Button,
  Card,
  Center,
  Checkbox,
  Chip,
  Code,
  Collapse,
  Container,
  Divider,
  Drawer,
  Grid,
  Group,
  List,
  Loader,
  Menu,
  Modal,
  MultiSelect,
  NativeSelect,
  NavLink,
  NumberInput,
  Paper,
  PasswordInput,
  Progress,
  RingProgress,
  ScrollArea,
  SegmentedControl,
  Select,
  SimpleGrid,
  Stack,
  Switch,
  Tabs,
  Text,
  TextInput,
  Textarea,
  ThemeIcon,
  Title,
  Tooltip,
  useMantineColorScheme,
} from '@mantine/core';
import {
  IconAlertCircle,
  IconAlertTriangle,
  IconArchive,
  IconArrowBackUp,
  IconBell,
  IconBolt,
  IconBrain,
  IconBriefcase,
  IconCalendar,
  IconChartBar,
  IconCheck,
  IconChevronDown,
  IconChevronUp,
  IconClock,
  IconCommand,
  IconDeviceMobile,
  IconDownload,
  IconEdit,
  IconFileText,
  IconFlag,
  IconFolder,
  IconGavel,
  IconHistory,
  IconMail,
  IconMailOff,
  IconMessageQuestion,
  IconMoonStars,
  IconNews,
  IconPaperclip,
  IconPencil,
  IconPhone,
  IconPlus,
  IconRadar,
  IconReload,
  IconReport,
  IconSchool,
  IconSearch,
  IconSend,
  IconSettings,
  IconShare,
  IconShield,
  IconSparkles,
  IconStar,
  IconStarFilled,
  IconTarget,
  IconTrash,
  IconUser,
  IconUserPlus,
  IconUsersGroup,
  IconWand,
  IconX,
} from '@tabler/icons-react';
import { type ActionInboxState } from '../../api.js';
import { useLayoutEffect } from 'react';
import { notifications } from '@mantine/notifications';
import { api, type AuthUser } from '../../api.js';
// Round 48 follow-up (2026-07-15): phase D1 of the AddinPage.tsx monolith
// split — shared types, host-bridge helpers, formatting utilities, and the
// notifications-history module side-effect, moved VERBATIM from AddinPage.tsx.
// Layering rule: shared ← panels ← AddinPage; panels never import each other.

// --- Bridge to the WebView2 host (C#).  Safe no-ops if not in WebView2. ---

export interface OutlookEmailContext {
  message_id?: string;
  subject?: string;
  from?: string;
  body?: string;
  received_at?: string;
  entry_id?: string;
  folder_path?: string;
  store_name?: string;
}

// Hoisted to module scope (round 35 hotfix) — was declared inside AddinPage
// but referenced by IndexPanel, which is a separate top-level component and
// has no closure access to AddinPage's body. The fix passes bulkQueueStatus
// to IndexPanel as a prop; this type needs to be visible to both.
export type BulkQueueStatus = Awaited<ReturnType<typeof api.addinIndexBulkStatus>>;

export type HostMessage =
  | { type: 'set_email'; email: OutlookEmailContext | null }
  | { type: 'set_token'; token: string; user?: AuthUser }
  | { type: 'reload' }
  | {
      type: 'index_state';
      running: boolean;
      auto_index_minutes: number;
      new_mail_hooked: boolean;
      current_folder: {
        path: string | null;
        name: string | null;
        item_count: number | null;
        last_indexed: string | null;
      };
    }
  | {
      type: 'index_started';
      folder: string;
      folder_name: string;
      total: number;
      incremental: boolean;
    }
  | {
      type: 'index_progress';
      processed: number;
      total: number;
      inserted: number;
      duplicates_skipped: number;
      skipped: number;
      rate_per_sec: number;
      elapsed_sec: number;
      eta_sec: number;
      paused?: boolean;
    }
  | {
      type: 'index_complete' | 'index_cancelled';
      folder: string;
      total: number;
      inserted: number;
      duplicates_skipped: number;
      skipped: number;
      elapsed_ms: number;
      incremental: boolean;
    }
  | { type: 'index_error'; error: string }
  | { type: 'index_preparing'; folder: string; folder_name: string; message: string }
  | { type: 'auth_expired'; phase?: string }
  | { type: 'index_paused' }
  | { type: 'index_resumed' }
  | { type: 'auto_index_tick'; target: string }
  // Round 28-AC Phase B: addin auto-update events.
  | {
      type: 'update_available' | 'update_required';
      installed_version: string;
      latest_version: string;
      download_url: string;
      sha256: string;
      size_bytes?: number;
      changelog?: string[];
      force_below_version?: string;
    }
  | {
      type: 'update_progress';
      phase: 'downloading' | 'verifying' | 'extracting' | 'starting' | 'error';
      percent?: number;
      downloaded?: number;
      total?: number;
      message?: string;
    }
  // 3.5.10: C# emits action_error when delete/move/auto-index fails. Pre-3.5.10
  // React had no handler — combined with optimistic toasts, users saw fake
  // "Deleted from Outlook" notifications when the operation actually failed.
  | { type: 'action_error'; error: string; action?: string; message_id?: string }
  | { type: 'action_success'; action?: string; message_id?: string }
  // 3.5.12: post-index body-fill pass can run for 1-2 hours background.
  // Pre-3.5.12 these all silently dropped — user had no idea why Outlook
  // was still slow long after "Index complete" fired.
  | { type: 'body_fill_started'; remaining_total: number }
  | { type: 'body_fill_progress'; folder: string; processed: number; repaired: number; elapsed_ms: number }
  | { type: 'body_fill_complete'; processed: number; repaired: number; elapsed_ms: number }
  | { type: 'body_fill_cancelled'; processed: number; repaired: number; elapsed_ms: number }
  | { type: 'body_fill_skipped'; reason: string }
  // 3.5.13: when Cached Exchange Mode shows fewer cached items than visible,
  // we fall back to slow per-item indexing. Pre-3.5.13 the user just saw
  // unexplained slowness and assumed the addin was broken.
  | { type: 'index_cache_warning'; folder: string; folder_name: string; cached_in_ost: number; visible_in_outlook: number; message: string }
  | {
      type: 'live_index';
      inserted: number;
      duplicates_skipped: number;
      total: number;
    }
  | {
      type: 'index_all_started';
      folder_count: number;
      folder_paths: string[];
    }
  | {
      type: 'index_all_folder';
      folder_index: number;
      folder_count: number;
      folder: string;
      folder_name: string;
    }
  | { type: 'index_all_complete'; folders_processed: number }
  | { type: 'addin_version'; version?: string }
  | {
      type: 'outlook_folders';
      folders: Array<{
        path: string;
        name: string;
        depth: number;
        item_count: number | null;
        last_indexed: string | null;
      }>;
      // C# sets this when enumeration throws — non-null means the list is partial or empty.
      error?: string | null;
      count?: number;
    };

declare global {
  interface Window {
    chrome?: {
      webview?: {
        postMessage: (msg: unknown) => void;
        addEventListener: (
          event: 'message',
          handler: (e: MessageEvent) => void,
        ) => void;
        removeEventListener: (
          event: 'message',
          handler: (e: MessageEvent) => void,
        ) => void;
      };
    };
  }
}

// Round 46: shows what's actually searchable. Users were confused that
// search "only returned current folder" — actually they'd only indexed
// one folder, so the corpus = 1 folder. This banner makes that obvious
// and one-clicks them into Mail sources to expand the index.

export function postToHost(msg: unknown): void {
  try {
    window.chrome?.webview?.postMessage(msg);
  } catch {
    /* not in webview */
  }
}

// 3.5.53 helper: when opening a brain row in Outlook, pass the full row's
// from_addr + received_at + subject. The addin's open-in-outlook handler
// uses these as a composite DASL search fallback when PR_INTERNET_MESSAGE_ID
// search misses (which it always does on IMAP/cPanel stores — PR_INTERNET_
// MESSAGE_ID is not an indexed property on Outlook's IMAP provider, so
// Items.Find / Items.Restrict can't filter on it).
//
// urn:schemas:httpmail:fromemail and urn:schemas:httpmail:datereceived ARE
// indexed everywhere — composite filter on those + a 2-second received_at
// window finds the email reliably across primary + shared mailboxes.
export function openEmailInOutlook(it: {
  message_id: string;
  from_addr?: string;
  received_at?: string;
  subject?: string | null;
  folder?: string | null;
}): void {
  postToHost({
    type: 'open_in_outlook',
    message_id: it.message_id,
    from_addr: it.from_addr ?? null,
    received_at: it.received_at ?? null,
    subject: it.subject ?? null,
    // 3.5.54: include folder path so the addin can walk just that
    // folder's Items + PropertyAccessor as a final fallback when DASL
    // search misses (IMAP stores don't index PR_INTERNET_MESSAGE_ID
    // and often not even composite urn:schemas:httpmail:* — per-item
    // iteration with PropertyAccessor is the only guaranteed path).
    folder: it.folder ?? null,
  });
}

// Pre-send guard: shows a confirmation if heuristic warnings fire, blocks
// "send" if any high-severity warning needs attention. Round 39 points 13–14.
export async function runPreSendCheck(input: {
  subject: string;
  to?: string;
  body: string;
  has_attachments?: boolean;
}): Promise<boolean> {
  let result: Awaited<ReturnType<typeof api.addinPreSendCheck>>;
  try {
    result = await api.addinPreSendCheck(input);
  } catch {
    return true; // fail open — don't block on guard outage
  }
  if (result.ok) return true;
  const lines = result.warnings
    .map((w) => `${w.severity === 'high' ? '⛔' : w.severity === 'medium' ? '⚠️' : 'ℹ️'} ${w.detail}`)
    .join('\n\n');
  const header = result.can_send
    ? 'Pre-send check found warnings. Send anyway?'
    : 'Pre-send check found high-severity issues. Are you sure you want to send?';
  return window.confirm(`${header}\n\n${lines}`);
}

// --- Tier helpers ---

export const TIER_COLOR: Record<string, string> = {
  P0: 'red',
  P1: 'orange',
  P2: 'yellow',
  P3: 'blue',
  P4: 'gray',
  FYI: 'gray',
  Newsletter: 'gray',
};

export function tierColor(tier?: string | null): string {
  if (!tier) return 'gray';
  return TIER_COLOR[tier] ?? 'gray';
}

export function sentimentColor(s: string): string {
  switch (s) {
    case 'urgent':
    case 'frustrated':
      return 'red';
    case 'negative':
      return 'orange';
    case 'positive':
      return 'green';
    default:
      return 'gray';
  }
}

// Round 31: surface the most common Anthropic-side errors in a friendlier
// way than "Draft failed". The brain forwards the raw message; we sniff for
// known patterns and rewrite into actionable copy.
export function friendlyAiError(e: unknown): string {
  const m = e instanceof Error ? e.message : String(e);
  if (/credit balance is too low/i.test(m))
    return 'Your Anthropic account has no credit balance. Go to console.anthropic.com → Plans & Billing → add credits, then retry. (The Outlook addin is fine; this is a billing-side block.)';
  if (/invalid x-api-key|api key not found/i.test(m))
    return 'Anthropic API key is invalid. Open the dashboard → Settings → Providers → re-paste the key.';
  if (/rate.?limit|429/i.test(m))
    return 'Anthropic rate-limit hit. Wait a few seconds and retry.';
  if (/timed out|timeout/i.test(m))
    return 'Anthropic request timed out. Network blip — retry.';
  if (/missing|not configured|not set/i.test(m))
    return 'Anthropic provider is not configured. Open the dashboard → Settings → Providers → enable Anthropic and paste a key.';
  return m || 'AI request failed';
}

export function shortDate(iso?: string | null): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso.slice(0, 10);
  const now = new Date();
  const time = d.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' });
  const sameDay = d.toDateString() === now.toDateString();
  if (sameDay) return time;
  const sameYear = d.getFullYear() === now.getFullYear();
  // Always include the time alongside the date so the user sees when the
  // email actually arrived, not just the day.
  const date = d.toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
    year: sameYear ? undefined : '2-digit',
  });
  return `${date} ${time}`;
}

export function formatDuration(sec: number): string {
  if (!Number.isFinite(sec) || sec <= 0) return '—';
  if (sec < 60) return `${Math.round(sec)}s`;
  if (sec < 3600) return `${Math.floor(sec / 60)}m ${Math.round(sec % 60)}s`;
  if (sec < 86400)
    return `${Math.floor(sec / 3600)}h ${Math.floor((sec % 3600) / 60)}m`;
  return `${Math.floor(sec / 86400)}d ${Math.floor((sec % 86400) / 3600)}h`;
}


// Notification history — capture every notifications.show() call into a
// localStorage-backed ring buffer + dispatch a custom event for the drawer.
export type NotifHistEntry = { id: string; ts: string; color: string; message: string };
export const notifHistory: NotifHistEntry[] = (() => {
  try {
    const raw = JSON.parse(localStorage.getItem('addin_notif_history') ?? '[]');
    if (Array.isArray(raw)) return raw.slice(-50) as NotifHistEntry[];
  } catch { /* ignore */ }
  return [];
})();
// Round 29: debounced localStorage write. Pre-fix: every notifications.show()
// triggered a synchronous JSON.stringify of notifHistory + localStorage write.
// During bulk operations (index complete, rule batches, dedup) toasts can
// burst at 5-20/sec, each blocking the UI thread on disk I/O. Now: collapse
// to one write per 1000ms regardless of burst rate. Also pageshow-flush
// guarantee on tab close so we never lose the latest entries.
let _notifPersistTimer: ReturnType<typeof setTimeout> | null = null;
export function _persistNotifHistoryNow() {
  try { localStorage.setItem('addin_notif_history', JSON.stringify(notifHistory)); } catch { /* ignore */ }
}
export function persistNotifHistory() {
  if (_notifPersistTimer != null) return;
  _notifPersistTimer = setTimeout(() => { _notifPersistTimer = null; _persistNotifHistoryNow(); }, 1000);
}
if (typeof window !== 'undefined') {
  window.addEventListener('pagehide', _persistNotifHistoryNow);
  window.addEventListener('beforeunload', _persistNotifHistoryNow);
}
export const ORIG_NOTIF_SHOW = notifications.show.bind(notifications);
// Object.assign is runtime-identical to `notifications.show = ...` but sidesteps
// the type-level `readonly` on the property (TS2540) without a cast.
Object.assign(notifications, {
  show: (opts: Parameters<typeof notifications.show>[0]) => {
  // Defend against rogue callers that pass an empty/missing message — those
  // render as a blank popup top-right with no readable text. Skip the toast
  // entirely AND skip the history entry; those are useless to both UI + log.
  const message = typeof opts === 'object' && opts != null
    ? String((opts as { message?: unknown }).message ?? '').trim()
    : '';
  if (!message) {
    return { id: '', message: '', color: '' };
  }
  const r = ORIG_NOTIF_SHOW(opts);
  try {
    const color = typeof opts === 'object' && opts != null
      ? String((opts as { color?: unknown }).color ?? 'blue')
      : 'blue';
    notifHistory.unshift({
      id: `n_${Date.now()}_${Math.random().toString(36).slice(2, 6)}`,
      ts: new Date().toISOString(),
      color, message,
    });
    if (notifHistory.length > 50) notifHistory.length = 50;
    persistNotifHistory();
    window.dispatchEvent(new CustomEvent('ai-outlook:notif-history-update'));
  } catch { /* swallow */ }
  return r;
  },
});

export function formatRelative(iso: string | null | undefined): string {
  if (!iso) return 'never';
  const t = new Date(iso).getTime();
  if (Number.isNaN(t)) return 'never';
  const ago = (Date.now() - t) / 1000;
  if (ago < 60) return 'just now';
  if (ago < 3600) return `${Math.round(ago / 60)} min ago`;
  if (ago < 86400) return `${Math.round(ago / 3600)}h ago`;
  return `${Math.round(ago / 86400)}d ago`;
}

// Strip Re:/Fwd: chains + bracket-tag clutter so list views show the actual
// subject. Returns { clean, prefix, tags } so the UI can render the prefix
// and tags as small badges if it wants to. Empty/whitespace input → '(no subject)'.
export function cleanSubject(raw: string | null | undefined): {
  clean: string;
  prefix: string | null; // 'Re' | 'Fwd' | etc., based on the outermost prefix chain
  tags: string[];        // [TICKET-123], [URGENT], etc.
} {
  const s = (raw ?? '').trim();
  if (!s) return { clean: '(no subject)', prefix: null, tags: [] };

  // Iteratively strip leading Re:/Fwd:/Aw:/Sv: chains. Record the OUTERMOST
  // prefix as the indicator (one badge regardless of nesting depth).
  const PREFIX_RE = /^\s*(Re|RE|re|Fwd|FWD|Fw|FW|fwd|fw|Aw|AW|aw|Sv|SV)\s*:\s*/;
  let body = s;
  let firstPrefix: string | null = null;
  for (let i = 0; i < 6; i++) {
    const m = body.match(PREFIX_RE);
    if (!m) break;
    if (!firstPrefix) firstPrefix = m[1]!.toLowerCase().startsWith('fw') ? 'Fwd' :
                                    m[1]!.toLowerCase().startsWith('aw') ? 'Aw'  :
                                    m[1]!.toLowerCase().startsWith('sv') ? 'Sv'  : 'Re';
    body = body.slice(m[0].length);
  }

  // Pull out leading [TAG] / (TAG) clusters as separate badges. Stop once a
  // non-bracket char appears so we don't strip mid-subject brackets.
  const tags: string[] = [];
  const TAG_RE = /^\s*[[(]\s*([^)\]]{1,32})\s*[\])]\s*/;
  for (let i = 0; i < 4; i++) {
    const m = body.match(TAG_RE);
    if (!m) break;
    tags.push(m[1]!.trim());
    body = body.slice(m[0].length);
  }

  const clean = body.trim() || '(no subject)';
  return { clean, prefix: firstPrefix, tags };
}

// --- Login form (used inline if no token) ---


export function parseHostMessage(data: unknown): HostMessage | null {
  try {
    const obj = typeof data === 'string' ? JSON.parse(data) : data;
    if (typeof obj !== 'object' || obj === null || !('type' in obj)) return null;
    return obj as HostMessage;
  } catch {
    return null;
  }
}


export function safeParse<T = unknown>(s: string): T | null {
  try {
    return JSON.parse(s);
  } catch {
    return null;
  }
}


// ---- bridge types + tab keep-alive (moved from AddinPage.tsx, phase D2) ----
export interface IndexState {
  running: boolean;
  auto_index_minutes: number;
  new_mail_hooked: boolean;
  current_folder: {
    path: string | null;
    name: string | null;
    item_count: number | null;
    last_indexed: string | null;
  };
}

export interface IndexProgress {
  processed: number;
  total: number;
  inserted: number;
  duplicates_skipped: number;
  skipped: number;
  rate_per_sec: number;
  elapsed_sec: number;
  eta_sec: number;
  paused?: boolean;
}

export interface BridgeBus {
  email: OutlookEmailContext | null;
  setEmail: (e: OutlookEmailContext | null) => void;
  indexState: IndexState | null;
  setIndexState: (s: IndexState | null) => void;
  progress: IndexProgress | null;
  setProgress: (p: IndexProgress | null) => void;
  lastEvents: BridgeEvent[];
  pushEvent: (e: BridgeEvent) => void;
  outlookFolders: Array<{
    path: string;
    name: string;
    depth: number;
    item_count: number | null;
    last_indexed: string | null;
  }>;
  setOutlookFolders: (
    f: Array<{
      path: string;
      name: string;
      depth: number;
      item_count: number | null;
      last_indexed: string | null;
    }>,
  ) => void;
  /** null = no response received yet (still waiting). Empty string = ok. Non-empty = C# reported an error. */
  outlookFoldersStatus: { received: boolean; error: string | null };
}

export interface BridgeEvent {
  ts: number;
  level: 'info' | 'success' | 'warn' | 'error';
  message: string;
}

// Mounts the child IMMEDIATELY on dashboard load (not lazy-on-first-visit
// like the previous version). Hidden via `display: none` when the tab
// isn't active. Effect: every panel's useEffect fires its API calls in
// the BACKGROUND while the user sees the welcome splash, so by the time
// they click any tab, data is already there. Eliminates the "click → spinner"
// pattern on every tab switch.
//
// Why we used to lazy-mount: defending against API thundering-herd at
// dashboard load. With the brain's async-queue + auto-retry in api.ts,
// a 10× concurrent fetch on mount is no longer a problem — the brain
// handles each request in <100 ms and reads use WAL snapshot (no
// writer-lock contention).
// Round 29: lazy-mount + keep-alive. The parent component (AddinPage) has
// ~20 state vars + 4 polling intervals (broadcasts, reminders, promises,
// indexState). Every interval fire and every dashboard interaction
// triggers a parent re-render, which previously re-ran the render
// function of all 17 panels — even ones the user had never opened.
// On heavy panels (Insights with charts, Search with 100 hits) that's
// hundreds of ms of wasted work per re-render, accumulating into the
// "everything is sluggish" sensation the user reported.
//
// Now: track per-name visited set in a top-level WeakRef-style cache.
// Until a tab is first activated, render null — no DOM, no children
// useEffects fire, no useMemo. Once activated, the panel mounts and
// stays mounted (display:none when inactive) so re-visits are instant
// and state survives. Hidden visited panels still re-render on parent
// state changes, but at least the panels the user has never touched
// don't pay the cost.
//
// The visited Set is module-scoped so it survives any internal re-
// renders of TabKeepAlive itself. Reset only on full page reload.
//
// LRU eviction (wave 3): pre-cap, every visited tab stayed mounted forever.
// With 19+ panels including heavy ones (Search, Insights, RiskFeed,
// CommandCenter), long Outlook sessions accumulated ~100-400 MB of live
// DOM. The cap keeps at most MAX_VISITED_TABS warm; older tabs unmount
// and reload-from-scratch on revisit. The typical workflow
// (brief → triage → workspace → ask → brief) stays fully warm; only the
// truly cold tabs pay the remount cost.
export const MAX_VISITED_TABS = 8;
export const _visitedTabs = new Set<string>();

export function touchTab(name: string): void {
  // Bump to MRU position. Set preserves insertion order, so delete-then-add
  // moves the entry to the end. When over cap, drop the oldest (first).
  if (_visitedTabs.has(name)) _visitedTabs.delete(name);
  _visitedTabs.add(name);
  while (_visitedTabs.size > MAX_VISITED_TABS) {
    const oldest = _visitedTabs.values().next().value;
    if (oldest === undefined) break;
    _visitedTabs.delete(oldest);
  }
}

export function TabKeepAlive({
  active,
  name,
  children,
}: { active: string; name: string; children: React.ReactNode }): React.JSX.Element | null {
  // May 2026: touchTab MUST run as a commit-time side effect, not during
  // render. React 19 concurrent rendering may re-invoke the render path;
  // mutating the module-scoped LRU during render produces non-deterministic
  // eviction order, and under StrictMode the doubled render can drop a
  // sibling tab mid-render of this one. useLayoutEffect runs after the DOM
  // commits and is synchronous (no flash of empty state). The conditional
  // hook is fine because `name` and the call site of TabKeepAlive don't
  // change per render — same hook count every time.
  useLayoutEffect(() => {
    if (active === name) touchTab(name);
  }, [active, name]);
  if (!_visitedTabs.has(name) && active !== name) return null;
  return (
    <div style={{ display: active === name ? 'block' : 'none' }}>
      {children}
    </div>
  );
}

// Round 29: broadcast banner / modal. Critical = blocking modal; lower
// severities = compact banner above the dashboard chrome. Multimedia:
// optional image, optional video (YouTube/Vimeo embed inferred from URL),
// optional CTA link button.

// ---- promoted from action-inbox (used by 8 panel modules) ----
export const ICON_PROPS = { size: 16, stroke: 1.5 } as const;
export const STATE_META: Record<
  ActionInboxState,
  { label: string; color: string; icon: React.JSX.Element; hint: string }
> = {
  act:      { label: 'Act',      color: 'red',    icon: <IconBolt size={14} />,    hint: 'Reply or decide today — VIPs, P0/P1, direct asks, recent questions.' },
  wait:     { label: 'Wait',     color: 'orange', icon: <IconClock size={14} />,   hint: 'Threads where you sent the latest reply. Nudge if overdue.' },
  know:     { label: 'Know',     color: 'blue',   icon: <IconBrain size={14} />,   hint: 'FYI — read for context, no action expected.' },
  automate: { label: 'Automate', color: 'violet', icon: <IconWand size={14} />,    hint: 'High-volume senders that look like rule candidates. Train or create a rule.' },
  ignore:   { label: 'Ignore',   color: 'gray',   icon: <IconArchive size={14} />, hint: 'Noise: machine senders, anti-VIPs, snoozed. Already filtered.' },
};
